import { Component, DestroyRef, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ErrorService } from '@shared/services/error.service';
import { SubscriptionManager } from '@shared/services/subscription-manager.service';
import { Subject } from 'rxjs';
import { InstanceIdService } from '@shared/services/instance-id.service';

@Component({
  selector: 'app-checkbox-group',
  templateUrl: './checkbox-group.component.html',
  styleUrls: ['./checkbox-group.component.scss'],
  standalone: false,
})
export class CheckboxGroupComponent implements OnInit, OnChanges {
  private instanceId: string;

  @Input() public formCtrl: FormControl = new FormControl();

  @Input() public height = 'fit-content';

  @Input() public width = 'fit-content';

  // layout : 'column' | 'row'
  @Input() public layout: string = 'column';

  @Input() public layoutAlign: string = 'default default';

  @Input() public gap: string = '10px';

  @Input() public label: string = '';

  @Input() public hintMessage: string = '';

  @Input() public triggerChangesSubject: Subject<void> = new Subject<void>();

  @Input() public elements: {
    label: string;
    value: any;
    isChecked: boolean;
    isDisabled?: boolean;
    priority?: number;
  }[] = [];

  @Input() public withSelector: boolean = false;

  @Input() public maintainTeams: boolean = false;

  @Input() public selectAllOption: boolean = false;

  @Input() public onlyOneAtATime = false;

  @Input() public disabled: boolean = false;

  @Output() public selectionChange = new EventEmitter<any>();

  public onOptionClick = new EventEmitter<string>();

  constructor(
    private errorService: ErrorService,
    private destroyRef: DestroyRef,
    private subscriptionManager: SubscriptionManager,
    private instanceIdService: InstanceIdService
  ) {
    this.instanceId = this.instanceIdService.generateId();
    this.subscriptionManager.registerComponent(this.instanceId, this.destroyRef);
  }

  ngOnInit() {
    if (this.maintainTeams) {
      this.elements?.forEach((elt: any) => {
        elt.isChecked = true;
      });
    }

    this.subscriptionManager
      .createRequest(this.triggerChangesSubject, 'triggerChangesSubject', this.instanceId)
      .subscribe({
        next: () => {
          this.refreshCheckboxButtons();
        },

        error: (error: any) => {
          this.errorService.handleError(error);
        },
      });
  }

  ngOnChanges(): void {
    this.refreshCheckboxButtons();
  }

  refreshCheckboxButtons(): void {
    const checkedElementsFromFormCtrl = this.formCtrl.value;
    const disabledElementsFromFormCtrl = this.formCtrl.disabled ?? false;
    if (checkedElementsFromFormCtrl && this.elements) {
      this.elements?.forEach((elt: any) => {
        // If the value of the checkbox is not a string we have to change the comparaison function
        if (this.withSelector) {
          const elem = checkedElementsFromFormCtrl.find((e: any) => e.value === elt?.value);
          if (elem && elt) {
            elt.isChecked = true;
            elt.priority = elem.priority;
            elt.isDisabled = disabledElementsFromFormCtrl;
          } else {
            elt.isChecked = false;
            if (this.disabled) {
              elt.isDisabled = true;
            }
          }
        } else if (elt) {
          if (checkedElementsFromFormCtrl.includes(elt?.value)) {
            elt.isChecked = true;
            elt.isDisabled = disabledElementsFromFormCtrl;
          } else {
            elt.isChecked = false;
            if (this.disabled) {
              elt.isDisabled = true;
            }
          }
        }
      });
    }
  }

  changeSelection() {
    this.fetchSelectedItems();
  }

  selectElement(element: any) {
    element.isChecked = !element?.isChecked;

    if (this.onlyOneAtATime && element.isChecked) {
      this.elements?.forEach((elt: any) => {
        if (elt.value !== element.value) {
          elt.isChecked = false;
        }
      });
    }
  }

  isAllSelected(rooms: any[]) {
    return rooms.every((room: any) => room.isChecked);
  }

  selectAll() {
    const allSelected = this.isAllSelected(this.elements);

    if (allSelected) {
      this.elements?.forEach((room: any) => (room.isChecked = false));
    } else {
      this.elements?.forEach((room: any) => {
        if (room.label !== 'Tout sélectionner') {
          room.isChecked = true;
        }
      });
    }
    this.changeSelection();
  }

  fetchSelectedItems() {
    let checkedElements;
    if (!this.withSelector) {
      checkedElements = this.elements
        ?.filter((value: any) => {
          return value.isChecked;
        })
        ?.map((element: any) => element.value);
    } else {
      checkedElements = this.elements
        ?.filter((value: any) => {
          return value.isChecked;
        })
        ?.map((element: any) => {
          return { value: element.value, priority: element.priority };
        });
    }

    this.formCtrl.setValue(checkedElements);

    this.selectionChange.emit(checkedElements);
  }
}
